import { useState, useEffect } from "react";
import { loadMain, loadMapui } from "../components/AMapComp/loader";
import currentPosIcon from "../components/AMapComp/assets/rescue-position-icon.png";

type Props = {
  container?: Element | null;
  geolocationIcon?: Element | null;
};

export default function useAMapHook(props: Props) {
  const { container } = props;
  const [loaded, setloaded] = useState(false);
  const [mapEntity, setMapEntity]: [any, Function] = useState(null);
  const [userCurrentPosition, setUserCurrentPosition] = useState<any>({});

  useEffect(() => {
    loadMain()
      .then(() => {
        return loadMapui();
      })
      .then(() => {
        setloaded(true);
      })
      .catch((err) => {
        console.error("Load AMap resource FAIL", err);
      });
  }, []);

  useEffect(() => {
    if (!loaded || !container) return;
    // 实例化地图对象
    setMapEntity(
      new window.AMap.Map(container, {
        resizeEnable: true,
        // center: [116.397428, 39.90923], // for testing
        zoom: 18,
      }),
    );
  }, [loaded, container]);

  useEffect(() => {
    if (!mapEntity) return;
    // 创建一个 Icon
    const geolocationIcon = new AMap.Icon({
      // 图标尺寸
      size: new AMap.Size(50, 50),
      // 图标的取图地址
      image: currentPosIcon,
      // 图标所用图片大小
      imageSize: new AMap.Size(50, 50),
      // 图标取图偏移量
      // imageOffset: new AMap.Pixel(-9, -3)
    });

    // 逆地理编码：将经纬度转为结构化地址
    const reverseGeocode = (lng: number, lat: number) => {
      AMap.plugin("AMap.Geocoder", () => {
        const geocoder = new AMap.Geocoder();
        geocoder.getAddress([lng, lat], (status, result) => {
          if (status === "complete" && result.regeocode) {
            const regeo = result.regeocode;
            setUserCurrentPosition({
              position: { lng, lat },
              formattedAddress: regeo.formattedAddress,
              addressComponent: regeo.addressComponent,
            });
          } else {
            setUserCurrentPosition({
              position: { lng, lat },
              formattedAddress: "",
              addressComponent: {},
            });
          }
          mapEntity.setCenter([lng, lat]);
        });
      });
    };

    // 浏览器原生定位降级：当高德定位失败时尝试 navigator.geolocation
    const fallbackBrowserGeolocation = () => {
      if (!navigator.geolocation) {
        fallbackIPLocation();
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lng = position.coords.longitude;
          const lat = position.coords.latitude;
          reverseGeocode(lng, lat);
        },
        () => {
          fallbackIPLocation();
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
      );
    };

    // IP 定位兜底：使用高德 CitySearch 获取城市级别定位
    const fallbackIPLocation = () => {
      AMap.plugin("AMap.CitySearch", () => {
        const citySearch = new AMap.CitySearch();
        citySearch.getLocalCity((status, result) => {
          if (status === "complete" && result.info === "OK") {
            const bounds = result.bounds;
            const center = bounds ? bounds.getCenter() : null;
            if (center) {
              reverseGeocode(center.lng, center.lat);
            }
          } else {
            console.warn("所有定位方式均失败");
          }
        });
      });
    };

    const geolocation = new AMap.Geolocation({
      enableHighAccuracy: true, //是否使用高精度定位，默认:true
      timeout: 10000, //超过10秒后停止定位，默认：无穷大
      maximumAge: 0, //定位结果缓存0毫秒，默认：0
      convert: true, //自动偏移坐标，偏移后的坐标为高德坐标，默认：true
      showButton: true, //显示定位按钮，默认：true
      buttonPosition: "RB", //定位按钮停靠位置，默认：'LB'，左下角
      buttonOffset: new AMap.Pixel(10, 20), //定位按钮与设置的停靠位置的偏移量，默认：Pixel(10, 20)
      showMarker: true, //定位成功后在定位到的位置显示点标记，默认：true
      showCircle: false, //定位成功后用圆圈表示定位精度范围，默认：true
      panToLocation: true, //定位成功后将定位到的位置作为地图中心点，默认：true
      zoomToAccuracy: true, //定位成功后调整地图视野范围使定位位置及精度范围视野内可见，默认：false
      markerOptions: {
        animation: "AMAP_ANIMATION_DROP",
        icon: geolocationIcon,
      },
    });
    mapEntity.addControl(geolocation);
    geolocation.getCurrentPosition(function (status, result) {
      if (status === "complete") {
        console.log("geolocation.getCurrentPosition", result);
        setUserCurrentPosition(result);
      } else {
        console.warn("高德定位失败，尝试降级定位", result);
        fallbackBrowserGeolocation();
      }
    });
    AMap.event.addListener(geolocation, "error", (error) => {
      console.log("geolocation.error >> ", error);
    });
  }, [mapEntity]);

  return {
    loaded,
    mapEntity,
    userCurrentPosition,
  };
}
